/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/order
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import { shopifyCountryCodeEnumOptions } from "./constants";
import {
  ShopifyDutyKey,
  ShopifyDutyToOrderLineItemKey,
  ShopifyDutyToRefundDutyKey,
  ShopifyDutyToShopKey,
  ShopifyOrderLineItemKey,
  ShopifyRefundDutyKey,
  ShopifyRefundDutyToDutyKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyDutyKey,
  "2022-01": {
    name: "Shopify Duty",
    fields: [
      {
        name: "Country Code Of Origin",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "country_code_of_origin" } },
      },
      {
        name: "Harmonized System Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "harmonized_system_code" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Order Line Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDutyToOrderLineItemKey,
        configuration: {
          relatedModelKey: ShopifyOrderLineItemKey,
        },
      },
      {
        name: "Shop Money",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_money" } },
      },
      {
        name: "Presentment Money",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "presentment_money" } },
      },
      {
        name: "Refund Duty",
        fieldType: FieldType.HasOne,
        key: ShopifyDutyToRefundDutyKey,
        configuration: {
          relatedModelKey: ShopifyRefundDutyKey,
          inverseFieldKey: ShopifyRefundDutyToDutyKey,
        },
      },
      {
        name: "Tax Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "tax_lines" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDutyToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "rest_list", resource: "order" },
  },
  "2025-04": {
    fields: [
      {
        name: "Country Code Of Origin",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "country_code_of_origin", graphQLSelection: "countryCodeOfOrigin", isShopifyEnum: true },
        },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: shopifyCountryCodeEnumOptions,
        },
      },
      {
        name: "Harmonized System Code",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "harmonized_system_code", graphQLFieldKey: "harmonizedSystemCode" },
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Order Line Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDutyToOrderLineItemKey,
        configuration: {
          relatedModelKey: ShopifyOrderLineItemKey,
        },
      },
      {
        name: "Shop Money",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "shop_money" },
        },
        isDeleted: true,
      },
      {
        name: "Presentment Money",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "presentment_money" },
        },
        isDeleted: true,
      },
      {
        name: "Refund Duty",
        fieldType: FieldType.HasOne,
        key: ShopifyDutyToRefundDutyKey,
        configuration: {
          relatedModelKey: ShopifyRefundDutyKey,
          inverseFieldKey: ShopifyRefundDutyToDutyKey,
        },
      },
      {
        name: "Tax Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "tax_lines", graphQLFieldKey: "taxLines" } },
      },
      {
        name: "Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "price_set", graphQLFieldKey: "price", graphQLOnlyField: true },
        },
      },
    ],
    syncConfig: {
      // Synced as part of ShopifyOrderLineItem
      type: "inherit",
    },
  },
};

export const ShopifyDuty = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
